import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "@/context/AuthContext";
import {
  House, UploadSimple, Stack, Users, FileText, CheckCircle, PaintBrush,
  Scales, ClipboardText, GearSix, SignOut, Receipt,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Dashboard", icon: House, testId: "nav-dashboard", roles: ["admin", "operator", "reviewer"] },
  { to: "/upload", label: "Upload Excel", icon: UploadSimple, testId: "nav-upload", roles: ["admin", "operator"] },
  { to: "/batches", label: "Import Batches", icon: Stack, testId: "nav-batches", roles: ["admin", "operator", "reviewer"] },
  { to: "/parties", label: "Issuers", icon: Users, testId: "nav-parties", roles: ["admin", "operator", "reviewer"] },
  { to: "/invoices/drafts", label: "Draft Invoices", icon: FileText, testId: "nav-drafts", roles: ["admin", "operator", "reviewer"] },
  { to: "/invoices/approved", label: "Approved Invoices", icon: CheckCircle, testId: "nav-approved", roles: ["admin", "operator", "reviewer"] },
  { to: "/templates", label: "Templates", icon: PaintBrush, testId: "nav-templates", roles: ["admin", "operator", "reviewer"] },
  { to: "/reconciliation", label: "Reconciliation", icon: Scales, testId: "nav-reconciliation", roles: ["admin", "operator", "reviewer"] },
  { to: "/audit", label: "Audit Log", icon: ClipboardText, testId: "nav-audit", roles: ["admin", "reviewer"] },
  { to: "/settings", label: "Settings", icon: GearSix, testId: "nav-settings", roles: ["admin"] },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => hasRole(user, ...n.roles));

  return (
    <div className="flex h-screen overflow-hidden">
      <aside className="sidebar w-60 flex flex-col shrink-0" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-[hsl(var(--sidebar-border))]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Receipt weight="duotone" size={20} />
            </div>
            <div>
              <div className="font-display text-sm font-semibold leading-tight">EvoPlus Invoice</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Invoice Suite</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {items.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground font-medium"
                    : "text-foreground/80 hover:bg-black/5"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[hsl(var(--sidebar-border))]">
          <div className="text-xs text-muted-foreground mb-2 truncate">
            {user?.email}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
            Role: <span className="text-foreground font-medium">{user?.role}</span>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-destructive px-2 py-1.5 rounded transition-colors"
          >
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
